import { TableV3ColDefs } from 'common/table';
import { ICellRendererParams } from 'ag-grid-community';
import { SourcingCandidateTableColumns } from 'modules/sourcing-shared/tables/types/SourcingCandidateTable';
import {
  SourcedCandidateStatus,
  CandidateSource,
  SearchableSourcedCandidateSortableFields,
  CandidateBatchStatus
} from 'generated-graphql/types';
import { Link, Typography } from '@mui/material';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import { SourcingRevealNumberLink } from 'modules/sourcing-shared/components/SourcingRevealNumberLink';
import { sourcingConversationStatusFilterOptions } from 'modules/sourcing-shared/constants/conversationStatusFilterOptions';
import { SourcingCandidateStatusTextComponent } from 'modules/sourcing-shared/components/SourcingCandidateStatusText';
import { sourcingStatusFilterOptions } from 'modules/sourcing-shared/constants/sourcingStatusFilterOptions';
import { SourcingConversationStatusIcon } from 'modules/sourcing-shared/components/SourcingConversationStatusIcon';
import { RouterLink } from 'common/ui/RouterLink';
import { Paths } from 'services/navigation/paths';

export const getSourcingCandidateTableColumns = ({
  onViewDetails,
  hideCandidateBatchJobTitleColumn
}: {
  onViewDetails: (
    row: SourcingCandidateTableColumns | undefined,
    index: number
  ) => void;
  hideCandidateBatchJobTitleColumn: boolean;
}): TableV3ColDefs<
  SourcingCandidateTableColumns,
  SearchableSourcedCandidateSortableFields
> => [
  {
    field: 'conversationStatus',
    headerName: ' ',
    width: 60,
    minWidth: 60,
    cellRenderer: (
      params: ICellRendererParams<SourcingCandidateTableColumns>
    ) => {
      if (!params.data) return null;

      return (
        <SourcingConversationStatusIcon
          sourcedCandidateStatus={params.data.status as SourcedCandidateStatus}
          conversationId={params.data.conversationId}
          conversationStatus={params.value}
          needsReview={!!params.data.conversationNeedsReview}
        />
      );
    },
    filterOptions: {
      items: sourcingConversationStatusFilterOptions,
      selectionType: 'multiple'
    }
  },
  {
    field: 'status',
    headerName: 'Invitation',
    width: 200,
    cellRenderer: (
      params: ICellRendererParams<SourcingCandidateTableColumns>
    ) => {
      if (!params.data) return null;
      return (
        <SourcingCandidateStatusTextComponent
          status={
            (params.data.status as SourcedCandidateStatus) ??
            SourcedCandidateStatus.EnrichmentFailed
          }
          candidateId={params.data.candidateId}
          candidateBatchId={params.data.candidateBatchId}
        />
      );
    },
    filterOptions: {
      items: sourcingStatusFilterOptions,
      selectionType: 'multiple'
    }
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    searchable: true,
    cellRenderer: (
      params: ICellRendererParams<SourcingCandidateTableColumns>
    ) => {
      return (
        <Link
          component="button"
          onClick={() => onViewDetails(params.data, params.node?.rowIndex ?? 0)}
        >
          {params.value ?? ''}
        </Link>
      );
    }
  },
  {
    field: 'location',
    headerName: 'Location',
    flex: 1,
    searchable: true
  },
  {
    field: 'candidateBatchJobTitle',
    headerName: 'Invited to',
    flex: 1,
    hide: hideCandidateBatchJobTitleColumn,
    cellRenderer: (
      params: ICellRendererParams<SourcingCandidateTableColumns>
    ) => {
      if (!params.data) return '-';
      const jobTitle = params.value;

      if (params.data.candidateBatchStatus === CandidateBatchStatus.Archived) {
        return <Typography>{jobTitle} - archived</Typography>;
      }

      return (
        <RouterLink
          to={`${Paths.ANALYSE}/${params.data.candidateBatchId}?tab=Source_candidates`}
        >
          {jobTitle}
        </RouterLink>
      );
    }
  },
  {
    field: 'phone',
    headerName: 'Phone',
    flex: 1,
    searchable: true,
    cellRenderer: (
      params: ICellRendererParams<SourcingCandidateTableColumns>
    ) => {
      if (!params.data) return null;

      if (params.data.linkedInProfileUrl) {
        return (
          <SourcingRevealNumberLink
            number={params.data.phoneNumber ?? undefined}
            fetchPhoneNumberAttempted={
              params.data.fetchPhoneNumberAttempted ?? false
            }
            sourcedCandidateId={params.data.id}
            linkedInProfileUrl={params.data.linkedInProfileUrl}
          />
        );
      }

      if (!params.data.phoneNumber) {
        return '-';
      }
      return (
        <Link href={`tel:${params.data.phoneNumber}`}>
          {params.data.phoneNumber}
        </Link>
      );
    }
  },
  {
    field: 'conversationRepliesCount',
    headerName: 'Replies',
    width: 100,
    sortField:
      SearchableSourcedCandidateSortableFields.ConversationRepliesCount,
    cellRenderer: (
      params: ICellRendererParams<SourcingCandidateTableColumns>
    ) => {
      return params.value ?? 0;
    }
  },
  {
    field: 'origin',
    headerName: 'Origin',
    flex: 1,
    filterOptions: {
      items: [
        {
          value: CandidateSource.ExternallySourcedViaContactOut,
          label: 'Popp external'
        },
        {
          value: CandidateSource.InternallySourcedViaAts,
          label: 'Popp internal'
        }
      ],
      selectionType: 'single'
    }
  },
  {
    field: 'id',
    headerName: '',
    width: 60,
    cellStyle: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    cellRenderer: (
      params: ICellRendererParams<SourcingCandidateTableColumns>
    ) => {
      if (!params.data) return null;

      const options: ActionOption[] = [
        {
          label: 'View details',
          onClick: () => onViewDetails(params.data, params.node?.rowIndex ?? 0),
          itemColor: 'primary'
        }
      ];
      return <ActionPopover id={params.data.id} options={options} />;
    }
  }
];
